import { IconCircleCheck } from "@tabler/icons-react";
import { usePrvTranslation } from "#/pages/ClientDetail/components/prv/usePrvTranslation";
import { type DataHealthReport, HEALTH_IMPACTS, type HealthImpact } from "#/types/dataHealth";
import { IMPACT_THEME } from "../healthCatalog";

interface Props {
  report: DataHealthReport;
  selected: HealthImpact | null;
  onSelect: (impact: HealthImpact | null) => void;
}

/**
 * One tile per impact group: how many records break it and what that costs.
 * A tile with findings filters the page to its group; clicking it again shows
 * everything. A clean group shows a green tick instead of a count.
 */
const HealthSummaryTiles = ({ report, selected, onSelect }: Props) => {
  const t = usePrvTranslation();

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {HEALTH_IMPACTS.map((impact) => {
        const theme = IMPACT_THEME[impact];
        const count = report.checks
          .filter((check) => check.impact === impact)
          .reduce((sum, check) => sum + check.count, 0);
        const active = selected === impact;
        const clean = count === 0;
        const Icon = clean ? IconCircleCheck : theme.icon;

        return (
          <button
            key={impact}
            type="button"
            disabled={clean}
            aria-pressed={active}
            onClick={() => onSelect(active ? null : impact)}
            className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-all ${
              clean
                ? "cursor-default border-gray-200 bg-white"
                : active
                  ? theme.tileSelected
                  : `${theme.tile} hover:shadow-sm`
            }`}
          >
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                clean ? "bg-emerald-100 text-emerald-700" : theme.iconWrap
              }`}
            >
              <Icon size={22} stroke={1.8} />
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span
                className={`text-2xl leading-none font-semibold tabular-nums ${
                  clean ? "text-emerald-700" : theme.count
                }`}
              >
                {clean ? t("dataHealth.inOrder") : count}
              </span>
              <span className="text-sm font-medium text-gray-900">
                {t(`dataHealth.impact.${impact}.title`)}
              </span>
              <span className="text-xs leading-snug text-gray-600">
                {t(`dataHealth.impact.${impact}.description`)}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default HealthSummaryTiles;
